import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { Formik, Form } from 'formik'
import * as y from 'yup'
import { toastr } from 'react-redux-toastr'
import { CalendarRange, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import FormikInput from '../../../components/form/FormikInput'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import FormikIsoDate from './FormikIsoDate'
import { todayIso } from '../lib/format'
import { describeScheduleError } from '../lib/errors'
import type { Schedule, ScheduleCalendar } from '../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  schedule?: Schedule          // present = edit mode
  onSaved?: (schedule: Schedule) => void
}

interface FormValues { name: string; description: string; startDate: string | null; defaultCalendarId: string; statusDate: string | null; status: 'DRAFT' | 'ACTIVE' }

const ScheduleFormDialog: React.FC<Props> = ({ open, onOpenChange, projectId, schedule, onSaved }) => {
  const { t } = useTranslation()
  const isEdit = !!schedule
  // Review fix (parked minor): Formik's own `isSubmitting` only reaches the Cancel/Save buttons
  // inside the render-prop — the Dialog's own close (Escape, outside click, the header X) sits
  // outside it, so a submit in flight could be dismissed mid-save without this.
  const [busy, setBusy] = useState(false)
  const { data: calData } = useQuery(Query.SCHEDULE_CALENDARS, { variables: { projectId }, skip: !open })
  const calendars: ScheduleCalendar[] = (calData?.scheduleCalendars ?? []).filter((c: ScheduleCalendar) => !c.archived)
  const [createSchedule] = useMutation(Mutation.CREATE_SCHEDULE, { refetchQueries: [{ query: Query.SCHEDULES, variables: { projectId, includeArchived: false } }, { query: Query.SCHEDULES, variables: { projectId, includeArchived: true } }] })
  const [updateSchedule] = useMutation(Mutation.UPDATE_SCHEDULE, { refetchQueries: [{ query: Query.SCHEDULES, variables: { projectId, includeArchived: false } }, { query: Query.SCHEDULES, variables: { projectId, includeArchived: true } }] })

  const standard = calendars.find((c) => c.name === 'Standard') ?? calendars[0]
  const initialValues: FormValues = {
    name: schedule?.name ?? '',
    description: schedule?.description ?? '',
    startDate: schedule?.startDate ?? todayIso(),
    defaultCalendarId: schedule?.defaultCalendarId ?? standard?.id ?? '',
    statusDate: schedule?.statusDate ?? null,
    status: schedule?.status === 'ACTIVE' ? 'ACTIVE' : 'DRAFT',
  }
  const validationSchema = y.object().shape({
    name: y.string().trim().required(t('Schedule_validation_name_required')).max(200),
    startDate: y.string().nullable().required(t('Schedule_validation_start_required')),
  })

  const handleSubmit = async (values: FormValues, { setSubmitting, setStatus }: any) => {
    setStatus(undefined)
    setBusy(true)
    try {
      if (isEdit) {
        const input: any = { name: values.name.trim(), description: values.description || null, startDate: values.startDate, defaultCalendarId: values.defaultCalendarId || undefined, statusDate: values.statusDate, status: values.status }
        const { data } = await updateSchedule({ variables: { id: schedule!.id, input } })
        toastr.success('', t('Schedule_saved'))
        onSaved?.(data.updateSchedule.schedule)
      } else {
        const input: any = { name: values.name.trim(), description: values.description || null, startDate: values.startDate, defaultCalendarId: values.defaultCalendarId || undefined }
        const { data } = await createSchedule({ variables: { projectId, input } })
        toastr.success('', t('Schedule_created'))
        onSaved?.(data.createSchedule)
      }
      onOpenChange(false)
    } catch (error: any) {
      setStatus({ submitError: describeScheduleError(error.message, t) })
    } finally {
      setSubmitting(false)
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onOpenChange(false) }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-primary" />
            {isEdit ? t('Schedule_edit_title') : t('Schedule_create_title')}
          </DialogTitle>
        </DialogHeader>
        <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ isSubmitting, status }) => (
            <Form className="space-y-4">
              <FormikInput type="text" name="name" label={t('Schedule_field_name')} />
              <FormikInput type="textarea" name="description" label={t('Schedule_field_description')} />
              <div className="grid grid-cols-2 gap-4">
                <FormikIsoDate name="startDate" label={t('Schedule_field_start_date')} />
                <FormikInput type="select" name="defaultCalendarId" label={t('Schedule_field_default_calendar')} options={calendars} labelField="name" valueField="id" />
              </div>
              {isEdit && (
                <div className="grid grid-cols-2 gap-4">
                  <FormikIsoDate name="statusDate" label={t('Schedule_field_status_date')} clearable />
                  <FormikInput type="select" name="status" label={t('Schedule_field_status')} options={[{ id: 'DRAFT', name: t('Schedule_status_DRAFT') }, { id: 'ACTIVE', name: t('Schedule_status_ACTIVE') }]} labelField="name" valueField="id" />
                </div>
              )}
              {status?.submitError && <p className="text-sm text-destructive">{status.submitError}</p>}
              <div className="flex items-center justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>{t('Common_cancel')}</Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
                  {isEdit ? t('Common_save') : t('Common_create')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
export default ScheduleFormDialog
